import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
} from 'recharts';
import {
  ChevronLeft,
  ChevronRight,
  DollarSign,
  Percent,
  Receipt,
  Search,
  TrendingDown,
  TrendingUp,
  X,
} from 'lucide-react';
import CrmStatCard from './CrmStatCard';
import { FIELD_CLASS, FilterSelect, TH_CLASS } from './CrmFilterUi';
import {
  buildClientPnlRows,
  buildMonthExpenseLedger,
  buildMonthlyPnl,
  buildRunRateSummary,
  formatCurrency,
  groupExpensesByPartner,
} from '../../utils/financeReportMetrics';
import { describeClientLifecycle } from '../../utils/accountLifecycle';
import { parseExpenseDate } from '../../services/accountExpensesService';

const COLLECTED_COLOR = '#16a34a';
const EXPENSE_COLOR = '#dc2626';
const PROFIT_COLOR = '#2563eb';

const CARD = 'rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm';

function formatSigned(value) {
  return value < 0 ? `-${formatCurrency(Math.abs(value))}` : formatCurrency(value);
}

function formatMargin(margin) {
  return margin === null || margin === undefined ? '—' : `${Math.round(margin * 100)}%`;
}

function fmtAxisCurrency(v) {
  const n = Math.abs(v);
  const sign = v < 0 ? '-' : '';
  if (n >= 1000) return `${sign}$${Math.round(n / 1000)}k`;
  return `${sign}$${n}`;
}

function profitClass(value) {
  return value < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400';
}

/** Tinted pill: red when losing money, amber under 30%, green otherwise. */
function MarginPill({ margin }) {
  if (margin === null || margin === undefined) {
    return <span className="text-gray-400">—</span>;
  }
  const tone =
    margin < 0
      ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
      : margin < 0.3
        ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800'
        : 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-semibold ${tone}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {formatMargin(margin)}
    </span>
  );
}

function StatusPill({ status }) {
  const tone =
    status === 'paid'
      ? 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800'
      : status === 'failed'
        ? 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800'
        : 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-xs font-medium capitalize ${tone}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-current" />
      {status}
    </span>
  );
}

function expenseSourceLabel(source, now) {
  const month = (offset) =>
    new Date(now.getFullYear(), now.getMonth() + offset, 1).toLocaleDateString('en-US', { month: 'short' });
  if (source === 'manual') return 'Manual';
  if (source === 'this-month') return `${month(0)} logged`;
  if (source === 'last-month') return `${month(-1)} logged`;
  return 'None logged';
}

const SORTS = {
  profit: { label: 'Monthly profit', get: (r) => r.monthlyProfit },
  margin: { label: 'Margin', get: (r) => (r.monthlyMargin ?? -Infinity) },
  mrr: { label: 'MRR', get: (r) => r.mrr },
  expenses: { label: 'Monthly expenses', get: (r) => r.monthlyExpenses },
  lifetime: { label: 'Lifetime profit', get: (r) => r.lifetimeProfit },
};

/**
 * Finance Reports ▸ Profit & Loss: MRR vs monthly expenses vs profit from the client accounts
 * (program/billing amounts, Billing-tab expenses, paid invoices) plus internal expenses.
 */
const FinanceProfitLossTab = ({ clientAccounts, paymentsByAccountId, expensesByAccountId, internalExpenses, darkMode }) => {
  const navigate = useNavigate();
  const now = useMemo(() => new Date(), []);

  const clientRows = useMemo(
    () => buildClientPnlRows({ clients: clientAccounts, paymentsByAccountId, expensesByAccountId, now }),
    [clientAccounts, paymentsByAccountId, expensesByAccountId, now],
  );
  const runRate = useMemo(() => buildRunRateSummary(clientRows, internalExpenses, now), [clientRows, internalExpenses, now]);
  const monthly = useMemo(
    () =>
      buildMonthlyPnl({
        clients: clientAccounts,
        paymentsByAccountId,
        expensesByAccountId,
        internalExpenses,
        now,
      }),
    [clientAccounts, paymentsByAccountId, expensesByAccountId, internalExpenses, now],
  );
  const monthlyTotals = useMemo(() => {
    const t = monthly.reduce(
      (acc, m) => ({ collected: acc.collected + m.collected, expenses: acc.expenses + m.expenses }),
      { collected: 0, expenses: 0 },
    );
    const profit = t.collected - t.expenses;
    return { ...t, profit, margin: t.collected > 0 ? profit / t.collected : null };
  }, [monthly]);

  // Expense drill-down month (defaults to the current month, the last row)
  const [selectedIdx, setSelectedIdx] = useState(monthly.length - 1);
  const selectedMonth = monthly[selectedIdx] || monthly[monthly.length - 1];
  const ledger = useMemo(
    () =>
      selectedMonth
        ? buildMonthExpenseLedger({
            clients: clientAccounts,
            expensesByAccountId,
            internalExpenses,
            monthStart: selectedMonth.monthStart,
          })
        : [],
    [clientAccounts, expensesByAccountId, internalExpenses, selectedMonth],
  );
  const byPartner = useMemo(() => groupExpensesByPartner(ledger), [ledger]);
  const maxPartnerAmount = Math.max(1, ...byPartner.map((p) => p.amount));

  // Client profitability table
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState('active');
  const [sortKey, setSortKey] = useState('profit');
  const [sortAsc, setSortAsc] = useState(false);
  const visibleClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    const sorter = SORTS[sortKey] || SORTS.profit;
    return clientRows
      .filter((r) => (scope === 'all' ? true : scope === 'ended' ? !r.active : r.active))
      .filter((r) => !q || r.clientName.toLowerCase().includes(q))
      .sort((a, b) => (sortAsc ? sorter.get(a) - sorter.get(b) : sorter.get(b) - sorter.get(a)));
  }, [clientRows, search, scope, sortKey, sortAsc]);
  const clientTotals = useMemo(
    () =>
      visibleClients.reduce(
        (acc, r) => ({
          mrr: acc.mrr + r.mrr,
          monthlyExpenses: acc.monthlyExpenses + r.monthlyExpenses,
          monthlyProfit: acc.monthlyProfit + r.monthlyProfit,
          lifetimeCollected: acc.lifetimeCollected + r.lifetimeCollected,
          lifetimeExpenses: acc.lifetimeExpenses + r.lifetimeExpenses,
          lifetimeProfit: acc.lifetimeProfit + r.lifetimeProfit,
        }),
        { mrr: 0, monthlyExpenses: 0, monthlyProfit: 0, lifetimeCollected: 0, lifetimeExpenses: 0, lifetimeProfit: 0 },
      ),
    [visibleClients],
  );

  useEffect(() => {
    setSelectedIdx(monthly.length - 1);
  }, [monthly.length]);

  const tooltipStyle = darkMode
    ? { backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8, color: '#f9fafb' }
    : { borderRadius: 8 };
  const axisTick = { fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' };

  const toggleSort = (key) => {
    if (sortKey === key) setSortAsc((v) => !v);
    else {
      setSortKey(key);
      setSortAsc(false);
    }
  };
  const sortHeader = (key, label, extra = '') => (
    <th className={`${TH_CLASS} whitespace-nowrap ${extra}`}>
      <button
        type="button"
        onClick={() => toggleSort(key)}
        className={`inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-900 dark:hover:text-white ${
          sortKey === key ? 'text-gray-900 dark:text-white' : ''
        }`}
      >
        {label}
        {sortKey === key && <span aria-hidden>{sortAsc ? '▲' : '▼'}</span>}
      </button>
    </th>
  );

  const internalSourceText =
    runRate.internalSource === 'none' ? 'none logged' : expenseSourceLabel(runRate.internalSource, now);

  return (
    <div className="space-y-8">
      {/* Run-rate */}
      <section>
        <div className="text-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Monthly run-rate</h2>
          <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">
            Active clients' monthly amounts minus what they cost us each month, plus internal costs.
          </p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <CrmStatCard icon={DollarSign} label="MRR" value={formatCurrency(runRate.mrr)} tone="green" />
          <CrmStatCard icon={Receipt} label="Monthly expenses" value={formatCurrency(runRate.expenses)} tone="red" />
          <CrmStatCard
            icon={runRate.profit < 0 ? TrendingDown : TrendingUp}
            label="Monthly profit"
            value={formatSigned(runRate.profit)}
            tone={runRate.profit < 0 ? 'red' : 'green'}
          />
          <CrmStatCard icon={Percent} label="Profit margin" value={formatMargin(runRate.margin)} tone="blue" />
        </div>
        <p className="text-xs text-center mt-3 text-gray-500 dark:text-gray-400">
          {runRate.activeClients} active clients · client costs {formatCurrency(runRate.clientExpenses)} · internal costs{' '}
          {formatCurrency(runRate.internalExpenses)} ({internalSourceText}). A client's manual Monthly Expenses (Billing
          tab) wins; otherwise the larger of this month's and last month's logged expenses is used.
        </p>
      </section>

      {/* Monthly actuals */}
      <section className={`${CARD} p-6`}>
        <div className="text-center mb-4">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Profit &amp; loss — last 12 months</h2>
          <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">
            Cash collected from paid client invoices vs. paid expenses, by month. Click a month to see its expenses.
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-6 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 text-sm">
          <span className="text-gray-600 dark:text-gray-300">
            Collected <strong className="text-gray-900 dark:text-white">{formatCurrency(monthlyTotals.collected)}</strong>
          </span>
          <span className="text-gray-600 dark:text-gray-300">
            Expenses <strong className="text-gray-900 dark:text-white">{formatCurrency(monthlyTotals.expenses)}</strong>
          </span>
          <span className="text-gray-600 dark:text-gray-300">
            Profit <strong className={profitClass(monthlyTotals.profit)}>{formatSigned(monthlyTotals.profit)}</strong>
          </span>
          <span className="text-gray-600 dark:text-gray-300">
            Margin <strong className="text-gray-900 dark:text-white">{formatMargin(monthlyTotals.margin)}</strong>
          </span>
        </div>

        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart
              data={monthly}
              margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
              onClick={(e) => {
                if (e && typeof e.activeTooltipIndex === 'number') setSelectedIdx(e.activeTooltipIndex);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={darkMode ? '#374151' : '#e5e7eb'} />
              <XAxis dataKey="label" tick={axisTick} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={fmtAxisCurrency} tick={axisTick} axisLine={false} tickLine={false} width={56} />
              <Tooltip
                formatter={(v, name) => [formatSigned(Number(v)), name]}
                contentStyle={tooltipStyle}
                cursor={{ fill: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <ReferenceLine y={0} stroke={darkMode ? '#4b5563' : '#d1d5db'} />
              <Bar dataKey="collected" name="Collected" fill={COLLECTED_COLOR} radius={[4, 4, 0, 0]} cursor="pointer" />
              <Bar dataKey="expenses" name="Expenses" fill={EXPENSE_COLOR} radius={[4, 4, 0, 0]} cursor="pointer" />
              <Line type="monotone" dataKey="profit" name="Profit" stroke={PROFIT_COLOR} strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        <div className="overflow-x-auto -mx-2 px-2 mt-6">
          <table className="min-w-[720px] w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/60">
              <tr>
                {['Month', 'Collected', 'Client costs', 'Internal costs', 'Total expenses', 'Profit', 'Margin'].map((h) => (
                  <th key={h} className={`${TH_CLASS} whitespace-nowrap`}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...monthly].reverse().map((m) => {
                const idx = monthly.indexOf(m);
                const selected = idx === selectedIdx;
                return (
                  <tr
                    key={m.key}
                    onClick={() => setSelectedIdx(idx)}
                    className={`border-b border-gray-100 dark:border-gray-800 cursor-pointer transition-colors ${
                      selected ? 'bg-red-50/70 dark:bg-red-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
                    }`}
                  >
                    <td className="px-2 sm:px-4 py-2.5 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                      {m.longLabel}
                      {m.isCurrent && <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">to date</span>}
                    </td>
                    <td className="px-2 sm:px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{formatCurrency(m.collected)}</td>
                    <td className="px-2 sm:px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{formatCurrency(m.clientExpenses)}</td>
                    <td className="px-2 sm:px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{formatCurrency(m.internalExpenses)}</td>
                    <td className="px-2 sm:px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{formatCurrency(m.expenses)}</td>
                    <td className={`px-2 sm:px-4 py-2.5 whitespace-nowrap font-semibold ${profitClass(m.profit)}`}>{formatSigned(m.profit)}</td>
                    <td className="px-2 sm:px-4 py-2.5 whitespace-nowrap">
                      <MarginPill margin={m.margin} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {/* Expenses for the selected month */}
      {selectedMonth && (
        <section className={`${CARD} p-6`}>
          <div className="flex items-center justify-center gap-3 mb-1">
            <button
              type="button"
              aria-label="Previous month"
              disabled={selectedIdx <= 0}
              onClick={() => setSelectedIdx((i) => Math.max(0, i - 1))}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800 disabled:opacity-30 disabled:pointer-events-none"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-white min-w-[240px] text-center">
              Expenses · {selectedMonth.longLabel}
            </h2>
            <button
              type="button"
              aria-label="Next month"
              disabled={selectedIdx >= monthly.length - 1}
              onClick={() => setSelectedIdx((i) => Math.min(monthly.length - 1, i + 1))}
              className="p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-300 dark:hover:text-white dark:hover:bg-gray-800 disabled:opacity-30 disabled:pointer-events-none"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-center mb-6 text-gray-600 dark:text-gray-400">
            {formatCurrency(selectedMonth.expenses)} paid out · {formatSigned(selectedMonth.profit)} profit on{' '}
            {formatCurrency(selectedMonth.collected)} collected
          </p>

          {ledger.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
              No expenses logged in {selectedMonth.longLabel}. Expenses come from each client's Billing tab and internal
              expenses on Partner pages.
            </div>
          ) : (
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              <div>
                <h3 className="text-sm font-semibold text-center mb-3 text-gray-700 dark:text-gray-200">By partner</h3>
                {byPartner.length === 0 ? (
                  <p className="text-xs text-center text-gray-500 dark:text-gray-400">No paid expenses yet this month.</p>
                ) : (
                  <ul className="space-y-3">
                    {byPartner.map((p) => (
                      <li key={p.key}>
                        <div className="flex items-baseline justify-between gap-2 text-sm">
                          <span className="font-medium text-gray-900 dark:text-white truncate">{p.name}</span>
                          <span className="font-semibold text-gray-900 dark:text-white shrink-0">{formatCurrency(p.amount)}</span>
                        </div>
                        <div className="h-2 mt-1 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
                          <div
                            className="h-full rounded-full bg-red-500"
                            style={{ width: `${(p.amount / maxPartnerAmount) * 100}%` }}
                          />
                        </div>
                        <div className="text-xs mt-1 text-gray-500 dark:text-gray-400 truncate">
                          {p.count} payment{p.count === 1 ? '' : 's'} · {p.clients.join(', ')}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="xl:col-span-2 overflow-x-auto -mx-2 px-2">
                <table className="min-w-[640px] w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-800/60">
                    <tr>
                      {['Date', 'Partner', 'Client', 'Description', 'Status', 'Amount'].map((h) => (
                        <th key={h} className={`${TH_CLASS} whitespace-nowrap`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {ledger.map((e) => (
                      <tr
                        key={`${e.accountId || 'internal'}-${e.id}`}
                        onClick={() => e.accountId && navigate(`/crm/client/${e.accountId}`)}
                        className={`border-b border-gray-100 dark:border-gray-800 ${
                          e.accountId ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50' : ''
                        }`}
                      >
                        <td className="px-2 sm:px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">
                          {parseExpenseDate(e)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) || '—'}
                        </td>
                        <td className="px-2 sm:px-4 py-2.5 whitespace-nowrap font-medium text-gray-900 dark:text-white">
                          {e.subcontractorName || 'Unassigned'}
                        </td>
                        <td className="px-2 sm:px-4 py-2.5 whitespace-nowrap text-gray-700 dark:text-gray-300">{e.clientName}</td>
                        <td className="px-2 sm:px-4 py-2.5 text-gray-500 dark:text-gray-400 max-w-[260px] truncate" title={e.description}>
                          {e.description || '—'}
                        </td>
                        <td className="px-2 sm:px-4 py-2.5 whitespace-nowrap">
                          <StatusPill status={e.status} />
                        </td>
                        <td className="px-2 sm:px-4 py-2.5 whitespace-nowrap font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(e.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs mt-2 text-gray-500 dark:text-gray-400">
                  Only paid expenses count toward totals; processing and failed ones are listed for reference.
                </p>
              </div>
            </div>
          )}
        </section>
      )}

      {/* Client profitability */}
      <section className={`${CARD} p-6`}>
        <div className="text-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white">Client profitability</h2>
          <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">
            Monthly numbers match each client's Billing tab. Lifetime = every paid invoice minus every paid expense.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" aria-hidden />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search clients…"
              className={`${FIELD_CLASS} pl-10 pr-9`}
            />
            {search && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <FilterSelect value={scope} onChange={(e) => setScope(e.target.value)} aria-label="Client scope">
            <option value="active">Active clients</option>
            <option value="ended">Ended clients</option>
            <option value="all">All clients</option>
          </FilterSelect>
          <FilterSelect icon={false} value={sortKey} onChange={(e) => { setSortKey(e.target.value); setSortAsc(false); }} aria-label="Sort by">
            {Object.entries(SORTS).map(([key, s]) => (
              <option key={key} value={key}>
                Sort: {s.label}
              </option>
            ))}
          </FilterSelect>
        </div>

        <div className="overflow-x-auto -mx-2 px-2">
          <table className="min-w-[1000px] w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/60">
              <tr>
                <th className={`${TH_CLASS} whitespace-nowrap`}>Client</th>
                <th className={`${TH_CLASS} whitespace-nowrap`}>Status</th>
                {sortHeader('mrr', 'MRR')}
                {sortHeader('expenses', 'Monthly expenses')}
                {sortHeader('profit', 'Monthly profit')}
                {sortHeader('margin', 'Margin')}
                <th className={`${TH_CLASS} whitespace-nowrap`}>Lifetime collected</th>
                <th className={`${TH_CLASS} whitespace-nowrap`}>Lifetime expenses</th>
                {sortHeader('lifetime', 'Lifetime profit')}
              </tr>
            </thead>
            <tbody>
              {visibleClients.map((r) => {
                const lc = describeClientLifecycle(r.lifecycleState);
                return (
                  <tr
                    key={r.accountId}
                    onClick={() => navigate(`/crm/client/${r.accountId}`)}
                    className="border-b border-gray-100 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="px-2 sm:px-4 py-3 whitespace-nowrap font-medium text-gray-900 dark:text-white">{r.clientName}</td>
                    <td className="px-2 sm:px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${lc?.className || ''}`}>
                        {lc?.label || r.lifecycleState}
                      </span>
                    </td>
                    <td className="px-2 sm:px-4 py-3 whitespace-nowrap font-medium text-gray-900 dark:text-white">{formatCurrency(r.mrr)}</td>
                    <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">
                      {formatCurrency(r.monthlyExpenses)}
                      {r.active && (
                        <span className="block text-[11px] text-gray-500 dark:text-gray-400">
                          {expenseSourceLabel(r.expenseSource, now)}
                        </span>
                      )}
                    </td>
                    <td className={`px-2 sm:px-4 py-3 whitespace-nowrap font-semibold ${profitClass(r.monthlyProfit)}`}>
                      {formatSigned(r.monthlyProfit)}
                    </td>
                    <td className="px-2 sm:px-4 py-3 whitespace-nowrap">
                      <MarginPill margin={r.monthlyMargin} />
                    </td>
                    <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{formatCurrency(r.lifetimeCollected)}</td>
                    <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-gray-700 dark:text-gray-300">{formatCurrency(r.lifetimeExpenses)}</td>
                    <td className={`px-2 sm:px-4 py-3 whitespace-nowrap font-semibold ${profitClass(r.lifetimeProfit)}`}>
                      {formatSigned(r.lifetimeProfit)}
                    </td>
                  </tr>
                );
              })}
              {visibleClients.length === 0 && (
                <tr>
                  <td colSpan={9} className="px-6 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    {search ? 'No clients match your search.' : 'No clients in this view.'}
                  </td>
                </tr>
              )}
            </tbody>
            {visibleClients.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 dark:bg-gray-800/60 font-semibold">
                  <td className="px-2 sm:px-4 py-3 text-gray-900 dark:text-white" colSpan={2}>
                    Total · {visibleClients.length} client{visibleClients.length === 1 ? '' : 's'}
                  </td>
                  <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-gray-900 dark:text-white">{formatCurrency(clientTotals.mrr)}</td>
                  <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-gray-900 dark:text-white">{formatCurrency(clientTotals.monthlyExpenses)}</td>
                  <td className={`px-2 sm:px-4 py-3 whitespace-nowrap ${profitClass(clientTotals.monthlyProfit)}`}>
                    {formatSigned(clientTotals.monthlyProfit)}
                  </td>
                  <td className="px-2 sm:px-4 py-3 whitespace-nowrap">
                    <MarginPill margin={clientTotals.mrr > 0 ? clientTotals.monthlyProfit / clientTotals.mrr : null} />
                  </td>
                  <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-gray-900 dark:text-white">{formatCurrency(clientTotals.lifetimeCollected)}</td>
                  <td className="px-2 sm:px-4 py-3 whitespace-nowrap text-gray-900 dark:text-white">{formatCurrency(clientTotals.lifetimeExpenses)}</td>
                  <td className={`px-2 sm:px-4 py-3 whitespace-nowrap ${profitClass(clientTotals.lifetimeProfit)}`}>
                    {formatSigned(clientTotals.lifetimeProfit)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>
    </div>
  );
};

export default FinanceProfitLossTab;
